import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { InputField } from "../../../components/forms/InputField";
import { QuestionWithRelations } from "../../../../../api/src/types";
import { validateCurrency } from "../../../utils/sharedValidators";
import {
  sanitizeCurrencyInput,
  parseAndFormatCurrency,
} from "../../../utils/format";
import { validateCurrencyWithRestriction } from "../utils/questionnaire";
import { useGetCurrentUserQuery } from "../../../api/users";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import Loader from "../../../components/icons/Loader";
import { InputWrapper, ErrorWrapper, IconContainer } from "./SharedFormStyles";
import GreenTick from "../../../components/icons/GreenTick";
import { IconSize } from "../../../components/theme";
import useIsMobile from "../../../hooks/useIsMobile";

interface CurrencyFieldInputProps {
  question: QuestionWithRelations;
}

const CurrencyFieldInput: React.FC<CurrencyFieldInputProps> = ({
  question,
}) => {
  const isMobile = useIsMobile();
  const [currencyInput, setCurrencyInput] = useState<string | null>(
    question.answers[0]?.valueTyped
      ? parseAndFormatCurrency(question.answers[0].valueTyped)
      : null,
  );
  const [currencyError, setCurrencyError] = useState<string | null>(null);

  const { data: user } = useGetCurrentUserQuery();
  const [
    createAnswer,
    {
      isLoading: isCreating,
      isError: isCreateError,
      isSuccess: isCreateSuccess,
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      isLoading: isUpdating,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  const hasAnswer = question.answers.length > 0;

  const save = useDebouncedCallback((value: string | null) => {
    if (!user) return;

    if (!hasAnswer && !value) return;

    const payload = {
      questionId: question.id,
      userId: user.id,
      valueTyped: value,
    };

    if (!hasAnswer) void createAnswer(payload);
    else void updateAnswer(payload);
  }, 1000);

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetCreateAnswer();
    resetUpdateAnswer();

    setCurrencyInput(e.target.value);
    // For empty values, only check required field validation
    if (e.target.value === "") {
      const restrictionErrorMessage = validateCurrencyWithRestriction(
        "",
        question,
      );
      setCurrencyError(restrictionErrorMessage);
      if (restrictionErrorMessage) {
        save(null);
        return;
      }
      save("");
      return;
    }

    // For non-empty values, validate basic currency format first
    const basicError = validateCurrency(e.target.value);
    if (basicError) {
      setCurrencyError(basicError);
      save(null);
      return;
    }

    // Then validate with input restrictions
    const normalizedValue = sanitizeCurrencyInput(e.target.value);
    const restrictionErrorMessage = validateCurrencyWithRestriction(
      normalizedValue,
      question,
    );
    setCurrencyError(restrictionErrorMessage);

    if (restrictionErrorMessage) {
      save(null);
      return;
    }
    save(normalizedValue); // normalized value saved to DB
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const normalizedValue = sanitizeCurrencyInput(e.target.value);

    // For empty values, only check required field validation
    if (normalizedValue === "") {
      const restrictionErrorMessage = validateCurrencyWithRestriction(
        "",
        question,
      );
      setCurrencyError(restrictionErrorMessage);
      if (restrictionErrorMessage) return;
      save("");
      return;
    }

    // For non-empty values, validate basic currency format first
    const basicError = validateCurrency(normalizedValue);
    if (basicError) {
      setCurrencyError(basicError);
      return;
    }

    // Then validate with input restrictions
    const restrictionErrorMessage = validateCurrencyWithRestriction(
      normalizedValue,
      question,
    );
    setCurrencyError(restrictionErrorMessage);
    if (restrictionErrorMessage) return;

    const formatted = parseAndFormatCurrency(normalizedValue);
    if (formatted) {
      setCurrencyInput(formatted);
      save(normalizedValue); // normalized value saved to DB
    }
  };

  // Clear the currency error when the input field is focused and user is typing
  const handleFocus = () => {
    setCurrencyError(null);
  };

  const isLoading = isCreating || isUpdating;
  const hasError = isCreateError || isUpdateError;
  const isSuccess =
    (isCreateSuccess || isUpdateSuccess) &&
    !hasError &&
    !isLoading &&
    !currencyError &&
    currencyInput !== "";

  return (
    <>
      <InputWrapper>
        <InputField
          type="text"
          inputMode="decimal" // Shows decimal keyboard on mobile
          value={currencyInput ?? ""}
          onChange={(e) => {
            handleCurrencyChange(e);
          }}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder="$0.00"
          errorMessage={currencyError}
          required={question.isRequired}
        />
        {isLoading && (
          <IconContainer>
            <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
          </IconContainer>
        )}
        {isSuccess && (
          <IconContainer>
            <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
          </IconContainer>
        )}
      </InputWrapper>
      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default CurrencyFieldInput;
